/**
 * Profile Page
 * Allows users to update their name and change their password
 */

import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { useAuthStore } from '@/stores/auth';
import { updateCurrentUser } from '@/api/users';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Separator } from '@/components/ui/separator';
import { User, KeyRound } from 'lucide-react';

interface NameFormValues {
    first_name: string;
    last_name: string;
}

interface PasswordFormValues {
    new_password: string;
    confirm_password: string;
}

export function ProfilePage() {
    const { user, setUser } = useAuthStore();
    const queryClient = useQueryClient();

    // --- Name form ---
    const {
        register: registerName,
        handleSubmit: handleSubmitName,
        formState: { errors: nameErrors, isDirty: isNameDirty },
    } = useForm<NameFormValues>({
        defaultValues: {
            first_name: user?.first_name ?? '',
            last_name: user?.last_name ?? '',
        },
    });

    const nameMutation = useMutation({
        mutationFn: (data: NameFormValues) =>
            updateCurrentUser({ first_name: data.first_name, last_name: data.last_name }),
        onSuccess: (updatedUser) => {
            setUser({
                ...user!,
                first_name: updatedUser.first_name ?? undefined,
                last_name: updatedUser.last_name ?? undefined,
            });
            queryClient.invalidateQueries({ queryKey: ['me'] });
            toast.success('Name updated successfully');
        },
        onError: () => {
            toast.error('Failed to update name');
        },
    });

    const onNameSubmit = (data: NameFormValues) => {
        nameMutation.mutate(data);
    };

    // --- Password form ---
    const [passwordVisible, setPasswordVisible] = useState(false);

    const {
        register: registerPassword,
        handleSubmit: handleSubmitPassword,
        reset: resetPassword,
        watch,
        formState: { errors: passwordErrors },
    } = useForm<PasswordFormValues>();

    const passwordMutation = useMutation({
        mutationFn: (data: PasswordFormValues) =>
            updateCurrentUser({ password: data.new_password }),
        onSuccess: () => {
            resetPassword();
            toast.success('Password changed successfully');
        },
        onError: () => {
            toast.error('Failed to change password');
        },
    });

    const onPasswordSubmit = (data: PasswordFormValues) => {
        passwordMutation.mutate(data);
    };

    const newPassword = watch('new_password');

    const displayName =
        [user?.first_name, user?.last_name].filter(Boolean).join(' ') || user?.email || 'User';

    return (
        <div className="max-w-2xl mx-auto space-y-6">
            <div>
                <h1 className="text-2xl font-semibold">Profile</h1>
                <p className="text-sm text-muted-foreground mt-1">
                    Manage your personal information and security settings.
                </p>
            </div>

            <Separator />

            {/* Name Card */}
            <Card>
                <CardHeader>
                    <div className="flex items-center gap-2">
                        <User className="h-5 w-5 text-muted-foreground" />
                        <CardTitle className="text-base">Display Name</CardTitle>
                    </div>
                    <CardDescription>Update how your name appears across the app.</CardDescription>
                </CardHeader>
                <CardContent>
                    <form onSubmit={handleSubmitName(onNameSubmit)} className="space-y-4">
                        <div className="grid grid-cols-2 gap-4">
                            <div className="space-y-2">
                                <Label htmlFor="first_name">First Name</Label>
                                <Input
                                    id="first_name"
                                    placeholder="First name"
                                    {...registerName('first_name')}
                                />
                                {nameErrors.first_name && (
                                    <p className="text-xs text-destructive">{nameErrors.first_name.message}</p>
                                )}
                            </div>
                            <div className="space-y-2">
                                <Label htmlFor="last_name">Last Name</Label>
                                <Input
                                    id="last_name"
                                    placeholder="Last name"
                                    {...registerName('last_name')}
                                />
                                {nameErrors.last_name && (
                                    <p className="text-xs text-destructive">{nameErrors.last_name.message}</p>
                                )}
                            </div>
                        </div>
                        <div className="flex justify-end">
                            <Button
                                type="submit"
                                disabled={nameMutation.isPending || !isNameDirty}
                                size="sm"
                            >
                                {nameMutation.isPending ? 'Saving…' : 'Save Name'}
                            </Button>
                        </div>
                    </form>
                </CardContent>
            </Card>

            {/* Password Card */}
            <Card>
                <CardHeader>
                    <div className="flex items-center gap-2">
                        <KeyRound className="h-5 w-5 text-muted-foreground" />
                        <CardTitle className="text-base">Change Password</CardTitle>
                    </div>
                    <CardDescription>Set a new password for your account.</CardDescription>
                </CardHeader>
                <CardContent>
                    <form onSubmit={handleSubmitPassword(onPasswordSubmit)} className="space-y-4">
                        <div className="space-y-2">
                            <Label htmlFor="new_password">New Password</Label>
                            <Input
                                id="new_password"
                                type={passwordVisible ? 'text' : 'password'}
                                placeholder="Enter new password"
                                autoComplete="new-password"
                                {...registerPassword('new_password', {
                                    required: 'New password is required',
                                    minLength: {
                                        value: 8,
                                        message: 'Password must be at least 8 characters',
                                    },
                                })}
                            />
                            {passwordErrors.new_password && (
                                <p className="text-xs text-destructive">
                                    {passwordErrors.new_password.message}
                                </p>
                            )}
                        </div>

                        <div className="space-y-2">
                            <Label htmlFor="confirm_password">Confirm Password</Label>
                            <Input
                                id="confirm_password"
                                type={passwordVisible ? 'text' : 'password'}
                                placeholder="Confirm new password"
                                autoComplete="new-password"
                                {...registerPassword('confirm_password', {
                                    required: 'Please confirm your password',
                                    validate: (value) =>
                                        value === newPassword || 'Passwords do not match',
                                })}
                            />
                            {passwordErrors.confirm_password && (
                                <p className="text-xs text-destructive">
                                    {passwordErrors.confirm_password.message}
                                </p>
                            )}
                        </div>

                        <div className="flex items-center gap-2">
                            <input
                                id="show_password"
                                type="checkbox"
                                className="h-4 w-4 rounded border accent-primary"
                                checked={passwordVisible}
                                onChange={(e) => setPasswordVisible(e.target.checked)}
                            />
                            <Label htmlFor="show_password" className="text-sm font-normal cursor-pointer">
                                Show password
                            </Label>
                        </div>

                        <div className="flex justify-end">
                            <Button
                                type="submit"
                                disabled={passwordMutation.isPending}
                                size="sm"
                            >
                                {passwordMutation.isPending ? 'Updating…' : 'Update Password'}
                            </Button>
                        </div>
                    </form>
                </CardContent>
            </Card>

            {/* Account Info (read-only) */}
            <Card>
                <CardHeader>
                    <CardTitle className="text-base">Account</CardTitle>
                    <CardDescription>Your account details.</CardDescription>
                </CardHeader>
                <CardContent className="space-y-3">
                    <div className="flex justify-between text-sm">
                        <span className="text-muted-foreground">Email</span>
                        <span className="font-medium">{user?.email}</span>
                    </div>
                    <div className="flex justify-between text-sm">
                        <span className="text-muted-foreground">Display name</span>
                        <span className="font-medium">{displayName}</span>
                    </div>
                </CardContent>
            </Card>
        </div>
    );
}
